import React, { useState, useEffect, useCallback } from 'react';
import {
  TrendingUp, TrendingDown, Calendar, DollarSign, BarChart3,
  PieChart, RefreshCw, ChevronLeft, ChevronRight, Award,
  Target, Activity, Percent, ArrowUpRight, ArrowDownRight,
  Minus, Globe, Flag
} from 'lucide-react';
import Layout from '../components/Layout';
import Loading from '../components/Loading';

/* ─────────────────────────────────────────────────────────
   공통 헬퍼
───────────────────────────────────────────────────────── */
const krwFmt = (v) =>
  new Intl.NumberFormat('ko-KR', { style: 'currency', currency: 'KRW', maximumFractionDigits: 0 }).format(v ?? 0);

const usdFmt = (v) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2 }).format(v ?? 0);

const pctFmt = (v) => `${(v ?? 0) >= 0 ? '+' : ''}${(v ?? 0).toFixed(2)}%`;

const profitColor  = (v) => v > 0 ? 'text-green-600' : v < 0 ? 'text-red-600' : 'text-gray-600';
const profitBg     = (v) => v > 0 ? 'bg-green-50 border-green-200' : v < 0 ? 'bg-red-50 border-red-200' : 'bg-gray-50 border-gray-200';
const profitIcon   = (v) =>
  v > 0 ? <ArrowUpRight className="w-4 h-4 text-green-600" /> :
  v < 0 ? <ArrowDownRight className="w-4 h-4 text-red-600" /> :
  <Minus className="w-4 h-4 text-gray-400" />;

/* ─────────────────────────────────────────────────────────
   막대 그래프 (공용)
   items: [{ label, value }]
───────────────────────────────────────────────────────── */
const BarChart = ({ items, fmtValue }) => {
  const maxAbs = Math.max(...items.map((i) => Math.abs(i.value)), 1);
  return (
    <div className="p-6 bg-gray-50">
      <div className="flex items-end justify-between h-48 space-x-1">
        {items.map((item, idx) => {
          const h   = (Math.abs(item.value) / maxAbs) * 100;
          const pos = item.value >= 0;
          return (
            <div key={idx} className="flex-1 flex flex-col items-center min-w-0">
              <div className="relative w-full h-40 flex items-end justify-center">
                {item.value !== 0 ? (
                  <div
                    className={`w-full rounded-t transition-all duration-500 ${pos ? 'bg-green-500' : 'bg-red-500'}`}
                    style={{ height: `${Math.max(h, 4)}%`, maxWidth: '32px' }}
                    title={`${item.label}: ${fmtValue(item.value)}`}
                  />
                ) : (
                  <div className="w-full h-1 bg-gray-300 rounded" style={{ maxWidth: '32px' }} />
                )}
              </div>
              <span className="text-xs text-gray-500 mt-2 truncate w-full text-center">{item.label}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
};

/* ─────────────────────────────────────────────────────────
   요약 카드 4개 (공용)
───────────────────────────────────────────────────────── */
const SummaryCards = ({ cards }) => (
  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
    {cards.map((c, i) => (
      <div key={i} className={`rounded-xl shadow-sm border-2 p-6 ${c.bg || 'bg-white border-gray-200'}`}>
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-medium text-gray-600">{c.label}</p>
            <p className={`text-3xl font-bold mt-2 ${c.valueClass || 'text-gray-900'}`}>{c.value}</p>
            {c.sub && <p className="text-xs text-gray-500 mt-1">{c.sub}</p>}
          </div>
          <div className={`p-3 rounded-xl ${c.iconBg || 'bg-gray-100'}`}>{c.icon}</div>
        </div>
      </div>
    ))}
  </div>
);

/* ═══════════════════════════════════════════════════════════
   탭 1 : 한국 주식
═══════════════════════════════════════════════════════════ */
const KrTab = ({ book = 'ALL' }) => {
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [yearsList,    setYearsList]    = useState([]);
  const [monthlyData,  setMonthlyData]  = useState([]);
  const [summary,      setSummary]      = useState(null);
  const [recentTrades, setRecentTrades] = useState([]);
  const [loading,      setLoading]      = useState(true);
  const [refreshing,   setRefreshing]   = useState(false);
  const [error,        setError]        = useState('');

  const monthNames = ['1월','2월','3월','4월','5월','6월','7월','8월','9월','10월','11월','12월'];
  const token = () => localStorage.getItem('token');

  const fetchYears = async () => {
    try {
      const r = await fetch(`/trading-api/performance/yearly-list?book=${book}`, { headers: { Authorization: `Bearer ${token()}` } });
      const d = await r.json();
      if (d.success) setYearsList(d.data);
    } catch {}
  };

  const fetchMonthly = async (year) => {
    try {
      const r = await fetch(`/trading-api/performance/monthly?year=${year}&book=${book}`, { headers: { Authorization: `Bearer ${token()}` } });
      const d = await r.json();
      if (d.success) {
        setMonthlyData(d.data.monthly || []);
        setSummary(d.data.yearly_summary || null);
      }
    } catch { setError('성과 데이터를 불러오는데 실패했습니다.'); }
  };

  const fetchRecent = async () => {
    try {
      const r = await fetch(`/trading-api/performance/recent-trades?limit=10&book=${book}`, { headers: { Authorization: `Bearer ${token()}` } });
      const d = await r.json();
      if (d.success) setRecentTrades(d.data);
    } catch {}
  };

  const loadAll = useCallback(async (isRefresh = false) => {
    isRefresh ? setRefreshing(true) : setLoading(true);
    setError('');
    await Promise.all([fetchYears(), fetchMonthly(selectedYear), fetchRecent()]);
    setLoading(false);
    setRefreshing(false);
  }, [selectedYear]);

  useEffect(() => { loadAll(); }, []);
  useEffect(() => { if (!loading) fetchMonthly(selectedYear); }, [selectedYear]);

  const changeYear = (dir) => {
    if (dir === 'prev' && yearsList.includes(selectedYear - 1)) setSelectedYear(y => y - 1);
    if (dir === 'next' && selectedYear < new Date().getFullYear()) setSelectedYear(y => y + 1);
  };

  if (loading) return <div className="flex justify-center py-12"><Loading size="large" message="성과 데이터를 불러오는 중..." /></div>;

  const summaryCards = summary ? [
    {
      label: '연간 총 실현손익', value: krwFmt(summary.total_profit),
      valueClass: profitColor(summary.total_profit), bg: profitBg(summary.total_profit),
      iconBg: summary.total_profit >= 0 ? 'bg-green-100' : 'bg-red-100',
      icon: summary.total_profit >= 0 ? <TrendingUp className="w-8 h-8 text-green-600" /> : <TrendingDown className="w-8 h-8 text-red-600" />,
    },
    { label: '연간 총 거래', value: `${summary.total_trades}회`, iconBg: 'bg-blue-100', icon: <Activity className="w-8 h-8 text-blue-600" /> },
    {
      label: '승률', value: `${(summary.win_rate ?? 0).toFixed(1)}%`,
      sub: `${summary.win_count}승 / ${summary.loss_count}패`, iconBg: 'bg-purple-100',
      icon: <Award className="w-8 h-8 text-purple-600" />,
    },
    {
      label: '평균 수익률', value: pctFmt(summary.avg_profit_rate),
      valueClass: profitColor(summary.avg_profit_rate), iconBg: 'bg-orange-100',
      icon: <Percent className="w-8 h-8 text-orange-600" />,
    },
  ] : [];

  return (
    <div className="space-y-6">
      {error && <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">{error}</div>}

      {/* 연도 선택 */}
      <div className="flex items-center justify-between bg-white rounded-lg shadow-sm border border-gray-200 p-4">
        <button onClick={() => changeYear('prev')} disabled={!yearsList.includes(selectedYear - 1)}
          className="p-2 rounded-lg hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed">
          <ChevronLeft className="w-6 h-6" />
        </button>
        <div className="flex items-center space-x-2">
          <Calendar className="w-5 h-5 text-blue-600" />
          <span className="text-2xl font-bold text-gray-900">{selectedYear}년</span>
        </div>
        <button onClick={() => changeYear('next')} disabled={selectedYear >= new Date().getFullYear()}
          className="p-2 rounded-lg hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed">
          <ChevronRight className="w-6 h-6" />
        </button>
      </div>

      {/* 연간 요약 */}
      {summary && <SummaryCards cards={summaryCards} />}

      {/* 월별 차트 + 테이블 */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="p-6 border-b border-gray-200 flex items-center space-x-2">
          <BarChart3 className="w-5 h-5 text-blue-600" />
          <h2 className="text-xl font-semibold text-gray-900">월별 실현손익</h2>
        </div>
        {monthlyData.length === 0 ? (
          <div className="p-12 text-center text-gray-500">
            <PieChart className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <p className="text-lg">{selectedYear}년 거래 내역이 없습니다.</p>
          </div>
        ) : (
          <>
            <BarChart
              items={monthNames.map((name, i) => {
                const d = monthlyData.find(m => m.month === i + 1);
                return { label: name, value: d?.total_profit ?? 0 };
              })}
              fmtValue={krwFmt}
            />
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    {['월','거래 횟수','매수 금액','매도 금액','실현손익','평균 수익률','승률'].map(h => (
                      <th key={h} className={`px-6 py-3 text-xs font-medium text-gray-500 uppercase ${h === '월' ? 'text-left' : 'text-right'}`}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {monthlyData.map((d) => (
                    <tr key={d.month} className="hover:bg-gray-50">
                      <td className="px-6 py-4 font-medium text-gray-900">{d.month}월</td>
                      <td className="px-6 py-4 text-right text-gray-900">{d.total_trades}회</td>
                      <td className="px-6 py-4 text-right text-gray-600">{krwFmt(d.total_buy_amount)}</td>
                      <td className="px-6 py-4 text-right text-gray-600">{krwFmt(d.total_sell_amount)}</td>
                      <td className={`px-6 py-4 text-right font-semibold ${profitColor(d.total_profit)}`}>
                        <div className="flex items-center justify-end">{profitIcon(d.total_profit)}<span className="ml-1">{krwFmt(d.total_profit)}</span></div>
                      </td>
                      <td className={`px-6 py-4 text-right ${profitColor(d.avg_profit_rate)}`}>{pctFmt(d.avg_profit_rate)}</td>
                      <td className="px-6 py-4 text-right">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${d.win_rate >= 50 ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                          {d.win_rate.toFixed(1)}%
                        </span>
                        <span className="text-xs text-gray-500 ml-2">({d.win_count}/{d.total_trades})</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>

      {/* 최근 거래 내역 */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="p-6 border-b border-gray-200 flex items-center space-x-2">
          <DollarSign className="w-5 h-5 text-green-600" />
          <h2 className="text-xl font-semibold text-gray-900">최근 거래 내역</h2>
        </div>
        {recentTrades.length === 0 ? (
          <div className="p-12 text-center text-gray-500">
            <Activity className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <p>거래 내역이 없습니다.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  {['일시','종목','수량','매수가','매도가','실현손익','수익률','사유'].map(h => (
                    <th key={h} className={`px-6 py-3 text-xs font-medium text-gray-500 uppercase ${h === '일시' || h === '종목' || h === '사유' ? 'text-left' : 'text-right'}`}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {recentTrades.map((t) => (
                  <tr key={t.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 text-sm text-gray-600 whitespace-nowrap">
                      {t.sell_date ? new Date(t.sell_date).toLocaleDateString('ko-KR') : '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <p className="text-sm font-medium text-gray-900">{t.stock_name}</p>
                      <p className="text-xs text-gray-500">{t.stock_code}</p>
                    </td>
                    <td className="px-6 py-4 text-right text-sm text-gray-900">{t.quantity?.toLocaleString()}주</td>
                    <td className="px-6 py-4 text-right text-sm text-gray-600">{krwFmt(t.avg_buy_price)}</td>
                    <td className="px-6 py-4 text-right text-sm text-gray-600">{krwFmt(t.sell_price)}</td>
                    <td className={`px-6 py-4 text-right text-sm font-medium ${profitColor(t.profit)}`}>
                      <div className="flex items-center justify-end">{profitIcon(t.profit)}<span className="ml-1">{krwFmt(t.profit)}</span></div>
                    </td>
                    <td className={`px-6 py-4 text-right text-sm font-medium ${profitColor(t.profit_rate)}`}>{pctFmt(t.profit_rate)}</td>
                    <td className="px-6 py-4">
                      <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded">{t.reason || '-'}</span>
                      {t.book && <BookBadge book={t.book} soldBy={t.sell_source} />}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* 안내 */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-start">
          <Target className="h-5 w-5 text-blue-600 mr-3 mt-0.5 shrink-0" />
          <div className="text-sm text-blue-800">
            <p className="font-medium mb-1">투자 성과 안내</p>
            <p className="text-blue-700">실현손익은 매도가 완료된 거래만 집계됩니다. 미체결 주문이나 보유 중인 종목의 평가손익은 포함되지 않습니다.</p>
          </div>
        </div>
      </div>
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════
   탭 2 : 미국 주식
═══════════════════════════════════════════════════════════ */
const UsTab = ({ book = 'ALL' }) => {
  const [data,      setData]      = useState(null);
  const [period,    setPeriod]    = useState('year');   // year | 6m | 3m | all
  const [loading,   setLoading]   = useState(true);
  const [refreshing,setRefreshing]= useState(false);
  const [error,     setError]     = useState('');

  const periodLabel = { year: '올해', '6m': '최근 6개월', '3m': '최근 3개월', all: '전체' };

  // 올해: 1월 1일부터 오늘까지의 일수 계산
  const getPeriodDays = (p) => {
    if (p === 'all')  return 0;
    if (p === '6m')   return 180;
    if (p === '3m')   return 90;
    if (p === 'year') {
      const now   = new Date();
      const jan1  = new Date(now.getFullYear(), 0, 1);
      return Math.ceil((now - jan1) / (1000 * 60 * 60 * 24)) + 1;
    }
    return 0;
  };

  const token = () => localStorage.getItem('token');

  const fetchData = useCallback(async (isRefresh = false) => {
    isRefresh ? setRefreshing(true) : setLoading(true);
    setError('');
    try {
      const days = getPeriodDays(period);
      const qs   = days > 0 ? `?days=${days}&book=${book}` : `?days=0&book=${book}`;
      const r    = await fetch(`/trading-api/us/trade/performance${qs}`, {
        headers: { Authorization: `Bearer ${token()}` },
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const d = await r.json();
      if (d.success) {
        setData(d);
      } else {
        setError(d.detail || '미국 주식 성과 조회 실패');
      }
    } catch (e) {
      setError(`데이터 조회 실패: ${e.message}`);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [period]);

  useEffect(() => { fetchData(); }, [period]);

  if (loading) return <div className="flex justify-center py-12"><Loading size="large" message="미국 주식 성과를 불러오는 중..." /></div>;

  const s = data?.summary;
  const summaryCards = s ? [
    {
      label: '총 실현손익 (USD)', value: usdFmt(s.total_realized_profit),
      valueClass: profitColor(s.total_realized_profit), bg: profitBg(s.total_realized_profit),
      iconBg: s.total_realized_profit >= 0 ? 'bg-green-100' : 'bg-red-100',
      icon: s.total_realized_profit >= 0 ? <TrendingUp className="w-8 h-8 text-green-600" /> : <TrendingDown className="w-8 h-8 text-red-600" />,
    },
    { label: '총 거래 횟수', value: `${s.total_trades}회`, iconBg: 'bg-blue-100', icon: <Activity className="w-8 h-8 text-blue-600" /> },
    {
      label: '승률', value: `${(s.win_rate_pct ?? 0).toFixed(1)}%`,
      sub: `${s.win_count}승 / ${s.loss_count}패`, iconBg: 'bg-purple-100',
      icon: <Award className="w-8 h-8 text-purple-600" />,
    },
    {
      label: '평균 수익률', value: pctFmt(s.avg_profit_rate),
      valueClass: profitColor(s.avg_profit_rate), iconBg: 'bg-orange-100',
      icon: <Percent className="w-8 h-8 text-orange-600" />,
    },
  ] : [];

  const monthly   = data?.monthly   || [];
  const byStock   = data?.by_stock  || [];
  const recentTr  = data?.recent    || [];

  return (
    <div className="space-y-6">
      {error && <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">{error}</div>}

      {/* 기간 필터 */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center space-x-2">
          <Globe className="w-5 h-5 text-blue-600" />
          <span className="font-semibold text-gray-800">조회 기간</span>
        </div>
        <div className="flex space-x-2">
          {Object.entries(periodLabel).map(([key, label]) => (
            <button
              key={key}
              onClick={() => setPeriod(key)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                period === key ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      {/* 요약 카드 */}
      {s ? (
        <>
          <SummaryCards cards={summaryCards} />

          {/* 추가 지표 */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5">
              <p className="text-sm text-gray-500 mb-1">최고 수익률</p>
              <p className={`text-2xl font-bold ${profitColor(s.best_profit_rate)}`}>{pctFmt(s.best_profit_rate)}</p>
            </div>
            <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5">
              <p className="text-sm text-gray-500 mb-1">최저 수익률</p>
              <p className={`text-2xl font-bold ${profitColor(s.worst_profit_rate)}`}>{pctFmt(s.worst_profit_rate)}</p>
            </div>
            <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5">
              <p className="text-sm text-gray-500 mb-1">총 매수 금액</p>
              <p className="text-2xl font-bold text-gray-900">{usdFmt(s.total_buy_amount_usd)}</p>
            </div>
          </div>
        </>
      ) : (
        <div className="bg-white rounded-xl border border-gray-200 p-12 text-center text-gray-500">
          <Globe className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <p className="text-lg">해당 기간에 미국 주식 거래 내역이 없습니다.</p>
        </div>
      )}

      {/* 월별 손익 추이 */}
      {monthly.length > 0 && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          <div className="p-6 border-b border-gray-200 flex items-center space-x-2">
            <BarChart3 className="w-5 h-5 text-blue-600" />
            <h2 className="text-xl font-semibold text-gray-900">월별 실현손익 (USD)</h2>
          </div>
          <BarChart
            items={[...monthly].reverse().map(m => ({ label: m.month?.slice(5) || m.month, value: m.monthly_profit }))}
            fmtValue={usdFmt}
          />
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  {['월','거래','실현손익 (USD)','평균 수익률','승률'].map(h => (
                    <th key={h} className={`px-6 py-3 text-xs font-medium text-gray-500 uppercase ${h === '월' ? 'text-left' : 'text-right'}`}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {[...monthly].reverse().map((m, i) => (
                  <tr key={i} className="hover:bg-gray-50">
                    <td className="px-6 py-4 font-medium text-gray-900">{m.month}</td>
                    <td className="px-6 py-4 text-right text-gray-900">{m.trade_count}회</td>
                    <td className={`px-6 py-4 text-right font-semibold ${profitColor(m.monthly_profit)}`}>
                      <div className="flex items-center justify-end">{profitIcon(m.monthly_profit)}<span className="ml-1">{usdFmt(m.monthly_profit)}</span></div>
                    </td>
                    <td className={`px-6 py-4 text-right ${profitColor(m.avg_profit_rate)}`}>{pctFmt(m.avg_profit_rate)}</td>
                    <td className="px-6 py-4 text-right">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${m.win_rate_pct >= 50 ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                        {(m.win_rate_pct ?? 0).toFixed(1)}%
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* 종목별 성과 */}
      {byStock.length > 0 && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          <div className="p-6 border-b border-gray-200 flex items-center space-x-2">
            <PieChart className="w-5 h-5 text-purple-600" />
            <h2 className="text-xl font-semibold text-gray-900">종목별 성과</h2>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  {['종목','거래','평균 매수가','평균 매도가','총 손익 (USD)','평균 수익률','최고/최저','승률'].map(h => (
                    <th key={h} className={`px-6 py-3 text-xs font-medium text-gray-500 uppercase ${h === '종목' ? 'text-left' : 'text-right'}`}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {byStock.map((s, i) => (
                  <tr key={i} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <p className="text-sm font-bold text-gray-900">{s.stock_code}</p>
                      <p className="text-xs text-gray-500">{s.stock_name !== s.stock_code ? s.stock_name : ''}</p>
                    </td>
                    <td className="px-6 py-4 text-right text-gray-900">{s.trade_count}회</td>
                    <td className="px-6 py-4 text-right text-gray-600">${s.avg_buy_price.toFixed(2)}</td>
                    <td className="px-6 py-4 text-right text-gray-600">${s.avg_sell_price.toFixed(2)}</td>
                    <td className={`px-6 py-4 text-right font-semibold ${profitColor(s.total_profit_usd)}`}>
                      <div className="flex items-center justify-end">{profitIcon(s.total_profit_usd)}<span className="ml-1">{usdFmt(s.total_profit_usd)}</span></div>
                    </td>
                    <td className={`px-6 py-4 text-right ${profitColor(s.avg_profit_rate)}`}>{pctFmt(s.avg_profit_rate)}</td>
                    <td className="px-6 py-4 text-right text-xs">
                      <span className="text-green-600">{pctFmt(s.best_profit_rate)}</span>
                      <span className="text-gray-400"> / </span>
                      <span className="text-red-600">{pctFmt(s.worst_profit_rate)}</span>
                    </td>
                    <td className="px-6 py-4 text-right">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${s.win_rate_pct >= 50 ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                        {(s.win_rate_pct ?? 0).toFixed(1)}%
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* 최근 거래 내역 */}
      {recentTr.length > 0 && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          <div className="p-6 border-b border-gray-200 flex items-center space-x-2">
            <DollarSign className="w-5 h-5 text-green-600" />
            <h2 className="text-xl font-semibold text-gray-900">최근 매도 내역</h2>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  {['일시','종목','수량','매수가','매도가','실현손익 (USD)','수익률','사유'].map(h => (
                    <th key={h} className={`px-6 py-3 text-xs font-medium text-gray-500 uppercase ${h === '일시' || h === '종목' || h === '사유' ? 'text-left' : 'text-right'}`}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {recentTr.map((t, i) => (
                  <tr key={i} className="hover:bg-gray-50">
                    <td className="px-6 py-4 text-sm text-gray-600 whitespace-nowrap">
                      {t.sell_date ? t.sell_date.toString().slice(0, 10) : '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <p className="text-sm font-bold text-gray-900">{t.stock_code}</p>
                      <p className="text-xs text-gray-500">{t.stock_name !== t.stock_code ? t.stock_name : ''}</p>
                    </td>
                    <td className="px-6 py-4 text-right text-sm text-gray-900">{t.sell_quantity}</td>
                    <td className="px-6 py-4 text-right text-sm text-gray-600">${t.avg_buy_price.toFixed(2)}</td>
                    <td className="px-6 py-4 text-right text-sm text-gray-600">${t.sell_price.toFixed(2)}</td>
                    <td className={`px-6 py-4 text-right text-sm font-medium ${profitColor(t.realized_profit)}`}>
                      <div className="flex items-center justify-end">{profitIcon(t.realized_profit)}<span className="ml-1">{usdFmt(t.realized_profit)}</span></div>
                    </td>
                    <td className={`px-6 py-4 text-right text-sm font-medium ${profitColor(t.profit_rate)}`}>{pctFmt(t.profit_rate)}</td>
                    <td className="px-6 py-4">
                      <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded">{t.sell_reason || '-'}</span>
                      {t.book && <BookBadge book={t.book} soldBy={t.sell_source} />}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-start">
          <Target className="h-5 w-5 text-blue-600 mr-3 mt-0.5 shrink-0" />
          <div className="text-sm text-blue-800">
            <p className="font-medium mb-1">미국 주식 성과 안내</p>
            <p className="text-blue-700">손익은 USD 기준이며, 실제 원화 손익은 환율에 따라 달라집니다. 실현손익은 매도 완료된 거래만 집계됩니다.</p>
          </div>
        </div>
      </div>
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════
   메인 페이지
═══════════════════════════════════════════════════════════ */
/* ─────────────────────────────────────────────────────────
   자동매매 손익 vs 개별매매 손익 (2026-10-06)
   book = 산 주체(손익 귀속). 자동매매로 산 걸 개인이 판 경우는 자동매매 손익에 넣고 따로 표시
───────────────────────────────────────────────────────── */
const BOOK_LABEL = { AUTO: '자동매매', MANUAL: '개별매매' };
const REASON_LABEL = {
  TAKE_PROFIT: '익절', STOP_LOSS: '손절', TREND_REVERSAL: '하락 전환', BOX_BREAKDOWN: '박스 하단 이탈',
  ACCUM_FAIL: '매집 무효', FRIDAY_LIQUIDATION: '금요일 청산', MANUAL_SELL: '개인 매도', QTY_ADJUST: '수량 보정', ETC: '기타',
};

const BookBadge = ({ book, soldBy }) => (
  <span className={`ml-1 text-[11px] px-1.5 py-0.5 rounded ${book === 'AUTO' ? 'bg-blue-100 text-blue-700' : 'bg-amber-100 text-amber-700'}`}>
    {BOOK_LABEL[book] || book}{book === 'AUTO' && soldBy === 'MANUAL' ? '·개인매도' : ''}
  </span>
);

const BookFilter = ({ value, onChange }) => (
  <div className="flex space-x-1 bg-gray-100 p-1 rounded-lg w-fit text-sm">
    {[['ALL', '전체'], ['AUTO', '자동매매'], ['MANUAL', '개별매매']].map(([k, label]) => (
      <button key={k} onClick={() => onChange(k)}
        className={`px-4 py-1.5 rounded-md ${value === k ? 'bg-white shadow-sm text-blue-700 font-medium' : 'text-gray-600'}`}>
        {label}
      </button>
    ))}
  </div>
);

const BookPerformancePanel = ({ market }) => {
  const [data, setData] = useState(null);
  const [days, setDays] = useState(3650);
  const [err,  setErr]  = useState('');
  const fmt = market === 'US' ? usdFmt : krwFmt;

  useEffect(() => {
    (async () => {
      try {
        const r = await fetch(`/trading-api/performance/auto-summary?days=${days}&market=${market}`,
          { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
        const d = await r.json();
        if (d.success) { setData(d); setErr(''); } else setErr(d.detail || '조회 실패');
      } catch (e) { setErr('조회 실패'); }
    })();
  }, [market, days]);

  const book = (k) => data?.by_book?.find(x => x.book === k) || { trades: 0, profit: 0 };
  const src  = (b, s) => data?.by_source?.find(x => x.book === b && x.sell_source === s);
  const total = (book('AUTO').profit || 0) + (book('MANUAL').profit || 0);
  const autoByManual = src('AUTO', 'MANUAL');
  const adjust = (data?.by_source || []).filter(x => x.sell_source === 'ADJUST').reduce((n, x) => n + x.trades, 0);

  const Card = ({ k, cls, children }) => {
    const b = book(k);
    return (
      <div className={`rounded-lg border p-4 ${cls}`}>
        <p className="text-sm font-semibold text-gray-800">{BOOK_LABEL[k]} 손익</p>
        <p className={`text-2xl font-bold mt-1 ${profitColor(b.profit || 0)}`}>{fmt(b.profit || 0)}</p>
        <p className="text-xs text-gray-600 mt-1">
          {b.trades}건 · 승률 {b.win_rate ?? '-'}{b.win_rate != null ? '%' : ''} · 수익률 {b.return_pct != null ? pctFmt(b.return_pct) : '-'}
        </p>
        {children}
      </div>
    );
  };

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
      <div className="flex items-center justify-between flex-wrap gap-2 mb-4">
        <div>
          <h2 className="text-xl font-semibold text-gray-900">실현손익: 자동매매 vs 개별매매</h2>
          <p className="text-xs text-gray-500 mt-1">기준선(실적 초기화) 이후 매도분. 손익은 '산 주체' 기준이며, 지금 보유분은 팔 때 원래 매입가 기준으로 잡힙니다.</p>
        </div>
        <select value={days} onChange={e => setDays(Number(e.target.value))}
          className="text-sm border border-gray-200 rounded-lg px-2 py-1">
          <option value={30}>최근 30일</option><option value={90}>최근 90일</option>
          <option value={365}>최근 1년</option><option value={3650}>전체</option>
        </select>
      </div>
      {err && <p className="text-sm text-yellow-700">{err}</p>}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card k="AUTO" cls="border-blue-200 bg-blue-50">
          {autoByManual?.trades > 0 && (
            <p className="text-xs text-amber-700 mt-2">
              └ 이 중 개인이 판 것 {autoByManual.trades}건 {fmt(autoByManual.profit || 0)}
            </p>
          )}
        </Card>
        <Card k="MANUAL" cls="border-amber-200 bg-amber-50" />
        <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
          <p className="text-sm font-semibold text-gray-800">합계</p>
          <p className={`text-2xl font-bold mt-1 ${profitColor(total)}`}>{fmt(total)}</p>
          {adjust > 0 && <p className="text-xs text-gray-500 mt-1">수량 보정 {adjust}건 (손익 없음, 미체결 등)</p>}
        </div>
      </div>

      {data?.by_reason?.filter(r => r.sell_source !== 'ADJUST').length > 0 && (
        <div className="mt-5 overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-xs text-gray-500 border-b">
              <tr><th className="text-left py-2">구분</th><th className="text-left">매도 사유</th><th className="text-right">건수</th>
                  <th className="text-right">승률</th><th className="text-right">손익</th><th className="text-right">수익률</th></tr>
            </thead>
            <tbody>
              {data.by_reason.filter(r => r.sell_source !== 'ADJUST').map((r, i) => (
                <tr key={i} className="border-b border-gray-50">
                  <td className="py-2"><BookBadge book={r.book} soldBy={r.sell_source} /></td>
                  <td>{REASON_LABEL[r.reason_code] || r.reason_code}</td>
                  <td className="text-right">{r.trades}</td>
                  <td className="text-right">{r.win_rate ?? '-'}{r.win_rate != null ? '%' : ''}</td>
                  <td className={`text-right ${profitColor(r.profit || 0)}`}>{r.profit != null ? fmt(r.profit) : '-'}</td>
                  <td className="text-right">{r.return_pct != null ? pctFmt(r.return_pct) : '-'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {data && !data.by_book?.length && (
        <p className="text-sm text-gray-400 mt-4">아직 기준선 이후 매도가 없습니다.</p>
      )}
    </div>
  );
};

const PerformancePage = () => {
  const [activeTab,   setActiveTab]   = useState('kr');
  const [book,        setBook]        = useState('ALL');   // 아래 상세: 전체 / 자동매매 / 개별매매
  const [refreshKey,  setRefreshKey]  = useState(0);
  const [isRefreshing,setIsRefreshing]= useState(false);

  const handleRefresh = () => {
    setIsRefreshing(true);
    setRefreshKey(k => k + 1);
    setTimeout(() => setIsRefreshing(false), 1500);
  };

  const tabs = [
    { id: 'kr', label: '🇰🇷 한국 주식', icon: <Flag className="w-4 h-4" /> },
    { id: 'us', label: '🇺🇸 미국 주식', icon: <Globe className="w-4 h-4" /> },
  ];

  return (
    <Layout>
      <div className="max-w-7xl mx-auto px-4 py-8 space-y-6">

        {/* 헤더 */}
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">투자 성과</h1>
            <p className="text-gray-600 mt-1">월별 실현손익 및 거래 분석</p>
          </div>
          <button
            onClick={handleRefresh}
            disabled={isRefreshing}
            className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-all"
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${isRefreshing ? 'animate-spin' : ''}`} />
            {isRefreshing ? '새로고침 중...' : '새로고침'}
          </button>
        </div>

        {/* 탭 */}
        <div className="flex space-x-1 bg-gray-100 p-1 rounded-xl w-fit">
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center space-x-2 px-6 py-2.5 rounded-lg text-sm font-medium transition-all ${
                activeTab === tab.id
                  ? 'bg-white text-blue-700 shadow-sm'
                  : 'text-gray-600 hover:text-gray-800'
              }`}
            >
              <span>{tab.label}</span>
            </button>
          ))}
        </div>

        {/* 탭 컨텐츠 */}
        <BookPerformancePanel key={`p-${activeTab}-${refreshKey}`} market={activeTab === 'kr' ? 'KR' : 'US'} />

        <div className="flex items-center justify-between flex-wrap gap-2">
          <h2 className="text-lg font-semibold text-gray-800">상세 실적</h2>
          <BookFilter value={book} onChange={setBook} />
        </div>
        <div key={`${activeTab}-${book}-${refreshKey}`} className="space-y-6">
          {activeTab === 'kr' ? <KrTab book={book} /> : <UsTab book={book} />}
        </div>

      </div>
    </Layout>
  );
};

export default PerformancePage;
